import React, { useContext } from "react";
import CustomButton from "../CustomButton";
import { ProjectContext } from "../../contexts/ProjectContextProvider";
import { createProject } from "../../api/Api";
import { showToast } from "../../utils/toastUtils";
import ModalUI from "../ui/ModalUI";
import { ProjectUpdateSchema } from "../../utils/validations";
import { useFormik } from "formik";
import ShortTextInput from "../Inputs/ShortTextInput";
import TextAreaInput from "../Inputs/TextAreaInput";
import { CURRENCY_OPTIONS } from "../../constants/constant";
import { useNavigate } from "react-router-dom";


function CreateNewProject({ show, handleClose }) {
  const {data:project, refresh } = useContext(ProjectContext);
  const navigate = useNavigate();

  const formik = useFormik({
    initialValues: {
      name: "",
      description: "",
      start_date: "",
      end_date: "",
      budget: project?.budget || "0.00",
      currency: project?.currency || "USD",
    },
    validationSchema: ProjectUpdateSchema,
    onSubmit: (values, { setSubmitting, resetForm }) => {
      handleNewProjectAdd(values, setSubmitting, resetForm);
    },
  });

  const handleNewProjectAdd = async (values, setSubmitting, resetForm ) => {
    setSubmitting(true);
    createProject(values)
      .then((response) => {
        if (response?.status === 200) {
          resetForm();
          handleClose();
          refresh();
          showToast("Project added successfully.", "success");
          navigate(`/project/${response?.data?.id}`);
        } else {
          showToast("Failed to add project. Please try again.", "error");
        }
      })
      .catch((error) => {
        showToast("Failed to add project. Please try again.", "error");
      })
      .finally(() => setSubmitting(false));
  };

  return (
    <CreateNewEntityModalUI
      show={show}
      handleClose={handleClose}
      formik={formik}
      buttonText="Add Project"
      title="Create New Project"
      loading={formik.isSubmitting}
    />
  );
}

export default CreateNewProject;

//---------------------------------------------------------------------------------------
// this is the UI for the modal to create a new project, organization, and workStream
export const CreateNewEntityModalUI = ({
  show,
  handleClose,
  formik,
  buttonText,
  entity="Project",
  title,
  loading,
}) => {
  const budgetValue = parseFloat(formik.values.budget || "0");
  const selectedCurrency = CURRENCY_OPTIONS.find(c => c.value === formik.values.currency) || CURRENCY_OPTIONS[0];
  return (
    <ModalUI show={show} handleClose={handleClose} title={title}>
      <form onSubmit={formik.handleSubmit}>
        <ShortTextInput
          id="name"
          label="Name"
          name="name"
          placeholder={`Enter ${entity} Name`}
          value={formik.values.name}
          onChange={formik.handleChange}
          error={formik.touched.name && formik.errors.name}
          required
        />
        <TextAreaInput
          id="description"
          label="Description"
          name="description"
          placeholder={`Enter ${entity} Description`}
          value={formik.values.description}
          onChange={formik.handleChange}
          error={formik.touched.description && formik.errors.description}
          required
        />

       {entity === "Project" && (
        <>
          <ShortTextInput
            id="budget"
            label="Budget"
            name="budget"
            type="number"
            value={formik.values.budget}
            onChange={formik.handleChange}
            error={formik.touched.budget && formik.errors.budget}
            prefix={budgetValue > 0 ? selectedCurrency.symbol : undefined}
          />
          {budgetValue > 0 && (
            <div className="mb-3">
              <label htmlFor="currency" className="form-label">Currency</label>
              <select
                id="currency"
                name="currency"
                className="form-select"
                value={formik.values.currency}
                onChange={formik.handleChange}
                required
              >
                {CURRENCY_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          )}
        </>
       )}
        <ShortTextInput
          id="start_date"
          label="Start Date"
          name="start_date"
          type="date"
          value={formik.values.start_date}
          onChange={formik.handleChange}
          error={formik.touched.start_date && formik.errors.start_date}
          required
        />
        <ShortTextInput
          id="end_date"
          label="End Date"
          name="end_date"
          type="date"
          value={formik.values.end_date}
          onChange={formik.handleChange}
          error={formik.touched.end_date && formik.errors.end_date}
          required
        />
        <div className="d-flex justify-content-center p-3">
          <CustomButton disabled={loading} type="submit">{buttonText}</CustomButton>
        </div>
      </form>
    </ModalUI>
  );
};
